import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, ManagedDataTable } from '@/design-system';
import type { Coupon } from '../api/coupons.service';
import { strategyLabel, valueLabel } from '../utils/coupon-format';

const USAGE_TYPE_LABELS: Record<string, string> = {
  CAMPAIGN: 'Campanha',
  INTERNAL: 'Retenção',
  AFFILIATE: 'Afiliado',
};

export interface CouponTableHandlers {
  onEdit: (coupon: Coupon) => void;
  onDeactivate: (coupon: Coupon) => void;
  onDelete: (coupon: Coupon) => void;
  onCreate: () => void;
}

interface CouponTableProps {
  coupons: Coupon[];
  loading: boolean;
  handlers: CouponTableHandlers;
}

export function CouponTable({ coupons, loading, handlers }: CouponTableProps) {
  const columns = useMemo<ColumnDef<Coupon>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Código',
        cell: ({ row }) => <span className="font-mono font-medium text-gray-900">{row.original.code}</span>,
      },
      {
        id: 'strategy',
        header: 'Benefício',
        cell: ({ row }) => <span className="text-sm text-gray-600">{strategyLabel(row.original)}</span>,
      },
      {
        accessorKey: 'usageType',
        header: 'Finalidade',
        cell: ({ row }) => (
          <span className="text-xs text-gray-500">
            {USAGE_TYPE_LABELS[row.original.usageType || 'CAMPAIGN'] || row.original.usageType || '—'}
          </span>
        ),
      },
      {
        id: 'value',
        header: 'Valor',
        cell: ({ row }) => <span className="text-sm font-medium">{valueLabel(row.original)}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Descrição',
        cell: ({ row }) => <span className="text-sm text-gray-500">{row.original.description ?? '—'}</span>,
      },
      {
        accessorKey: 'redeemedCount',
        header: 'Resgates',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.redeemedCount}
            {row.original.maxRedemptions != null ? ` / ${row.original.maxRedemptions}` : ''}
          </span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              row.original.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {row.original.isActive ? 'Ativo' : 'Inativo'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm"
                type="button"
                onClick={() => handlers.onEdit(c)}
                className="text-[#0084FF] hover:underline"
              >
                Editar
              </Button>
              {c.isActive ? (
                <Button variant="ghost" size="sm"
                  type="button"
                  onClick={() => handlers.onDeactivate(c)}
                  className="text-amber-600 hover:text-amber-800"
                >
                  Desativar
                </Button>
              ) : null}
              {c.redeemedCount === 0 ? (
                <Button variant="ghost" size="sm"
                  type="button"
                  onClick={() => handlers.onDelete(c)}
                  className="text-red-600 hover:text-red-800"
                >
                  Excluir
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [handlers]
  );

  return (
    <ManagedDataTable
      title="Lista de cupons"
      subtitle="Crie cupons de campanha ou retenção para descontos e períodos de teste no checkout."
      columns={columns}
      data={coupons}
      loading={loading}
      searchPlaceholder="Buscar cupom..."
      toolbarButtons={[
        {
          label: 'Novo cupom',
          icon: <Plus size={16} />,
          onClick: handlers.onCreate,
        },
      ]}
      clientPagination
      emptyMessage="Nenhum cupom cadastrado."
    />
  );
}
